import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, CheckCircle2, Palette, Sparkles } from 'lucide-react';
import { clsx } from 'clsx';
import { readWorkbookJson, writeActivityScore, writeWorkbookJson, type WorkbookActivityCompletion } from './workbookPersist';

export interface InfoCardProps {
  workbookLabel?: string;
  title: string;
  instructions?: string;
  content: string[];
  sourcePage?: number;
  uiHint?: string;
  persistenceId?: string;
  onInteracted?: () => void;
  onWorkbookStatusChange?: (status: WorkbookActivityCompletion) => void;
}

type InfoPersist = { done: boolean };

const cardColors = [
  'from-violet-400 to-app-primary/90',
  'from-fuchsia-400 to-app-primary/75',
  'from-amber-400 to-orange-500',
  'from-emerald-400 to-teal-500',
  'from-sky-400 to-blue-500',
];

export const InfoCard: React.FC<InfoCardProps> = ({
  workbookLabel,
  title,
  instructions,
  content,
  sourcePage,
  uiHint,
  persistenceId,
  onInteracted,
  onWorkbookStatusChange,
}) => {
  const storageKey = persistenceId ? `g3-info-done-${persistenceId}` : '';
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!storageKey) {
      setDone(false);
      return;
    }
    const loaded = readWorkbookJson<Partial<InfoPersist> | null>(storageKey, null);
    setDone(loaded?.done === true);
  }, [storageKey]);

  useEffect(() => {
    if (!storageKey) return;
    writeWorkbookJson(storageKey, { done } satisfies InfoPersist);
  }, [storageKey, done]);

  useEffect(() => {
    if (!onWorkbookStatusChange) return;
    onWorkbookStatusChange(done ? 'completed' : 'not_started');
  }, [onWorkbookStatusChange, done]);

  useEffect(() => {
    if (!persistenceId || !done) return;
    writeActivityScore(persistenceId, 100);
  }, [persistenceId, done]);

  const mark = useCallback(() => onInteracted?.(), [onInteracted]);

  const hasLines = content.length > 0 && content.some((c) => c.trim());

  return (
    <div className="overflow-hidden rounded-[2rem] border-4 border-app-primary/30/90 bg-gradient-to-br from-violet-50 via-fuchsia-50 to-purple-50 shadow-[0_20px_50px_-12px_rgba(139,92,246,0.35)] dark:border-app-primary/30/60 dark:from-violet-950/40 dark:via-slate-900 dark:to-slate-900">
      <div className="relative border-b border-app-primary/20/70 bg-white/55 px-5 py-5 backdrop-blur-sm dark:border-app-primary/30/40 dark:bg-slate-900/50">
        <div className="absolute right-4 top-4 opacity-20">
          <Palette className="h-24 w-24 text-app-primary" aria-hidden />
        </div>
        <div className="relative flex flex-wrap items-start gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-app-primary to-purple-600 text-white shadow-lg">
            <BookOpen className="h-9 w-9" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            {workbookLabel ? (
              <p className="text-sm font-extrabold text-app-primary tamil-text dark:text-violet-300" lang="ta">
                {workbookLabel}
              </p>
            ) : null}
            <h2 className="mt-1 text-2xl font-extrabold leading-snug text-app-primary tamil-text md:text-3xl dark:text-violet-50" lang="ta">
              {title}
            </h2>
            {instructions ? (
              <p className="mt-3 text-lg leading-relaxed text-app-primary/95 tamil-text dark:text-violet-100/90" lang="ta">
                {instructions}
              </p>
            ) : null}
            {sourcePage != null ? (
              <span className="mt-3 inline-block rounded-full bg-app-primary px-3 py-1 text-sm font-bold text-white">p.{sourcePage}</span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="px-4 py-8 sm:px-8">
        {hasLines ? (
          <div className="grid gap-4 sm:grid-cols-1">
            {content.map((line, i) => {
              if (!line.trim()) return null;
              const grad = cardColors[i % cardColors.length];
              return (
                <div
                  key={i}
                  className={clsx(
                    'rounded-3xl border-4 border-white/80 bg-gradient-to-r p-5 text-lg font-bold leading-relaxed text-white shadow-xl tamil-text',
                    grad
                  )}
                  lang="ta"
                >
                  <span className="mr-2 inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/25 text-base font-black">
                    {i + 1}
                  </span>
                  {line}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-3xl border-4 border-dashed border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-8 text-center dark:border-amber-700 dark:from-amber-950/40 dark:to-orange-950/30">
            <Sparkles className="mx-auto h-14 w-14 text-amber-500" aria-hidden />
            <p className="mt-4 text-xl font-extrabold leading-relaxed text-amber-950 tamil-text dark:text-amber-50" lang="ta">
              இந்தச் செயல் வகுப்பறையில் / புத்தகத்தில் ஆசிரியர் வழிகாட்டுதலுடன் செய்யப்படும்.
            </p>
            <p className="mt-3 text-lg text-amber-900/90 tamil-text dark:text-amber-100/80" lang="ta">
              நீங்கள் வீட்டில் படங்களைப் பார்த்து, பேச்சுடன் பயிற்சி செய்யலாம்!
            </p>
            {uiHint ? (
              <p className="mt-4 rounded-2xl bg-white/70 px-3 py-2 text-sm font-semibold text-amber-900 dark:bg-slate-800/80 dark:text-amber-200">
                ({uiHint})
              </p>
            ) : null}
          </div>
        )}

        <div className="mt-8 flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={() => {
              mark();
              setDone(true);
            }}
            disabled={done}
            className={clsx(
              'inline-flex min-h-[52px] items-center justify-center gap-2 rounded-2xl px-8 py-3 text-lg font-extrabold shadow-lg transition tamil-text',
              done
                ? 'border-4 border-emerald-400 bg-emerald-100 text-emerald-900 dark:border-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-50'
                : 'border-4 border-violet-400 bg-gradient-to-r from-app-primary to-fuchsia-500 text-white hover:brightness-110 active:scale-[0.98]'
            )}
            lang="ta"
          >
            <CheckCircle2 className="h-6 w-6 shrink-0" aria-hidden />
            {done ? 'முடிந்தது ✓' : 'படித்து முடித்தேன்'}
          </button>
        </div>
      </div>
    </div>
  );
};
